import { Interaction } from '../models/customer-session/Interaction';
import {
    InteractionFilter,
    InteractionPatternItem
} from '../models/customer-session/InteractionPattern';
// @ts-expect-error https://github.com/handlebars-lang/handlebars.js/issues/1733
import Handlebars from 'handlebars/dist/cjs/handlebars';
import { ActiveHelpScenario } from '../models/ActiveHelpScenario';
import store from '../store/store';
import { getDb } from './IndexedDbService';
import { getCurrentSession } from './CustomerSessionService';
import { FreeShippingPolicy } from '../models/FreeShippingPolicy';
import { apiClient } from './ApiClient';
import { Product } from '../models/Product';
import type { ActiveHelp } from '../models/ActiveHelp';

export type OmitSessionId<Type> = {
    [Property in keyof Type as Exclude<Property, 'sessionId'>]: Type[Property];
};

[
    ['EQUALS', (a: unknown, b: unknown) => a === b],
    ['AND', (...params: unknown[]) => params.every(p => !!p)],
    [
        'MULTIPLY',
        (...params: number[]) =>
            params
                .slice(0, -1)
                .reduce((previous, current) => previous * current, 1)
    ],
    ['IS_NULL', (a: unknown) => a === null],
    ['NOT', (a: boolean) => !a],
    ['SUBSTRACT', (a: number, b: number) => a - b],
    [
        'BETWEEN',
        (value: number, min: number, max: number) =>
            value <= max && value >= min
    ],
    ['CURRENCY', (a: number) => Math.round(a * 100) / 100],
    [
        'MAP_PROPERTY',
        (array: Record<string, unknown>[], property: string) =>
            array.map(item => item[property])
    ],
    ['JSON_STRINGIFY', (value: unknown) => JSON.stringify(value)],
    ['LENGTH', (a?: unknown[]) => a?.length || 0]
].forEach(([name, fn]) => Handlebars.registerHelper(name, fn));

let freeShippingPolicy: FreeShippingPolicy | undefined = undefined;

export function setFreeShippingPolicy(fsp?: FreeShippingPolicy): void {
    freeShippingPolicy = fsp;
}

export async function addInteraction(
    interaction: OmitSessionId<Interaction>
): Promise<number> {
    const session = await getCurrentSession(true);
    const result = await getDb().add('interactions', {
        ...interaction,
        sessionId: session.id
    });
    await checkScenarios();
    return result;
}

export async function checkScenarios(): Promise<void> {
    const scenarios: ActiveHelpScenario[] = [
        {
            name: 'Welcome-Message',
            tags: ['Welcome-Message'],
            activeHelpDefinition: {
                title: `¡Hola! 👋 ¿Tienes alguna pregunta?`,
                description: `Estoy aquí para ayudar`,
                activeHelpType: 'open-main-menu',
                iconId: null
            },
            disabling: {
                alwaysDisabled: true,
                hashExpression: '', // will use scenario literal name as hash
                timeWindow: {
                    type: 'session'
                }
            },
            interactionPattern: {
                timeWindow: {
                    type: 'session'
                },
                lastInteractionFilter: {
                    interactionType: 'page-view',
                    filterExpression: `true`,
                    dependencyWithValueKeys: [],
                    exportedValues: []
                },
                items: []
            }
        },
        {
            name: 'Interest-Product-1',
            tags: ['Interest-Product'],
            activeHelpDefinition: {
                title: `¡Hola! ¿Sabías que puedes preguntarme lo que quieras sobre este producto?`,
                description: `Dime qué duda tienes y te respondo al instante ⚡`,
                iconId: 'help',
                activeHelpType: 'product-detail',
                productId: '{{vars.productId}}',
                questionText: 'Me gustaría saber si '
            },
            disabling: {
                alwaysDisabled: true,
                hashExpression: `Interest-Product|{{vars.productId}}`,
                timeWindow: {
                    type: 'session'
                }
            },
            interactionPattern: {
                timeWindow: {
                    type: 'duration',
                    durationMilliseconds: 10 * 60 * 1000
                },
                lastInteractionFilter: {
                    interactionType: 'page-view',
                    filterExpression: `
                {{EQUALS interaction.pageType 'product'}}
                `,
                    dependencyWithValueKeys: [],
                    exportedValues: [
                        {
                            key: 'productId',
                            type: 'string',
                            getterExpression: `
                    {{interaction.product.productId}}
                    `
                        }
                    ]
                },
                items: [
                    {
                        nextGapType: 'non-consecutive',
                        interactionFilter: {
                            interactionType: 'page-view',
                            filterExpression: `
                    {{AND (EQUALS interaction.pageType 'product') (EQUALS interaction.product.productId vars.productId)}}
                    `,
                            dependencyWithValueKeys: ['productId'],
                            exportedValues: []
                        }
                    }
                ]
            }
        },
        {
            name: 'Interest-Product-2',
            tags: ['Interest-Product'],
            activeHelpDefinition: {
                title: `¡Hola! ¿Sabías que puedes preguntarme lo que quieras sobre este producto?`,
                description: `Dime qué duda tienes y te respondo al instante ⚡`,
                iconId: 'help',
                activeHelpType: 'product-detail',
                productId: '{{vars.productId}}',
                questionText: 'Me gustaría saber si '
            },
            disabling: {
                alwaysDisabled: true,
                hashExpression: `Interest-Product|{{vars.productId}}`,
                timeWindow: {
                    type: 'session'
                }
            },
            interactionPattern: {
                timeWindow: {
                    type: 'session'
                },
                lastInteractionFilter: {
                    interactionType: 'page-active',
                    filterExpression: `
                {{AND (EQUALS interaction.pageType 'product') (EQUALS interaction.seconds 10)}}
                `,
                    dependencyWithValueKeys: [],
                    exportedValues: [
                        {
                            key: 'productId',
                            type: 'string',
                            getterExpression: `
                    {{interaction.product.productId}}
                    `
                        }
                    ]
                },
                items: []
            }
        },
        {
            name: 'Crosselling-Complementary-On-Add-To-Cart-1',
            tags: [],
            activeHelpDefinition: {
                title: `¡Buena elección!`,
                description: `Complementa tu compra con alguna de estas ideas`,
                iconId: 'favorite-full',
                activeHelpType: 'product-search-combinations',
                combinationWithProductIds: `["{{interaction.newCartLine.productId}}"]`
            },
            disabling: {
                alwaysDisabled: false,
                hashExpression: '', // will use scenario literal name as hash
                timeWindow: {
                    type: 'duration',
                    durationMilliseconds: 0
                }
            },
            interactionPattern: {
                timeWindow: {
                    type: 'session'
                },
                lastInteractionFilter: {
                    interactionType: 'cart-line-addition',
                    filterExpression: `
                {{IS_NULL interaction.previousCartLine}}
                `,
                    dependencyWithValueKeys: [],
                    exportedValues: []
                },
                items: []
            }
        },
        {
            name: 'Promo-Free-Shipping-On-Cart-Visit-1',
            tags: [],
            activeHelpDefinition: {
                title: `¡Te faltan sólo {{vars.difference}}€ para tener envío gratuito! 📦`,
                description: `Complementa tu compra con alguna de estas ideas`,
                iconId: 'shipping-full',
                activeHelpType: 'product-search-combinations',
                combinationWithProductIds: `{{{JSON_STRINGIFY (MAP_PROPERTY cart.lines 'productId')}}}`
            },
            disabling: {
                alwaysDisabled: false,
                hashExpression: '', // will use scenario literal name as hash
                timeWindow: {
                    type: 'duration',
                    durationMilliseconds: 0
                }
            },
            interactionPattern: {
                timeWindow: {
                    type: 'session'
                },
                lastInteractionFilter: {
                    interactionType: 'page-view',
                    filterExpression: `
                {{AND (EQUALS interaction.pageType 'cart') (BETWEEN (SUBSTRACT freeShippingPolicy.minimumAmount cart.totalPrice) 0.01 (MULTIPLY 0.01 freeShippingPolicy.notificationThreshold freeShippingPolicy.minimumAmount))}}
                `,
                    dependencyWithValueKeys: [],
                    exportedValues: [
                        {
                            key: 'difference',
                            type: 'number',
                            getterExpression: `
                        {{CURRENCY (SUBSTRACT freeShippingPolicy.minimumAmount cart.totalPrice)}}
                        `
                        }
                    ]
                },
                items: []
            }
        }
    ];
    const session = await getCurrentSession(false);
    for (const scenario of scenarios.filter(s => !s.disabling.alwaysDisabled)) {
        const { interactionPattern } = scenario;
        let vars = {};
        const interactions = (
            await getDb().getAllFromIndex(
                'interactions',
                'by-date',
                interactionPattern.timeWindow.type === 'session'
                    ? undefined
                    : IDBKeyRange.lowerBound(
                          new Date(
                              new Date().getTime() -
                                  interactionPattern.timeWindow
                                      .durationMilliseconds
                          )
                      )
            )
        ).filter(i => i.sessionId === session.id); // TODO: investigate if sessionId can be filtered on IndexedDB query
        const lastInteraction = interactions[interactions.length - 1];
        if (
            evaluateFilter(
                lastInteraction,
                interactionPattern.lastInteractionFilter
            )
        ) {
            vars = getExportedValues(
                lastInteraction,
                interactionPattern.lastInteractionFilter
            );
            const result = evaluateItems(
                interactions.slice(0, -1),
                interactionPattern.items,
                vars
            );
            if (result.matches) {
                const hash = evaluateHash(scenario, lastInteraction, vars);
                if (!(await isEnabledScenario(scenario, hash, session.id))) {
                    continue;
                }
                const proceedResult = await proceedScenario(
                    scenario,
                    lastInteraction,
                    vars
                );
                if (!proceedResult.success) {
                    continue;
                }

                store.dispatch.activeHelpState.showIfAssistantIsMinified({
                    scenarioName: scenario.name,
                    activeHelp: proceedResult.activeHelp
                });
                await addExecutedScenario(
                    hash,
                    session.id,
                    scenario.name,
                    vars
                );
            }
        }
    }
}

function evaluateItems(
    interactions: Interaction[],
    items: InteractionPatternItem[],
    vars: Record<string, unknown>
): {
    matches: boolean;
    vars: Record<string, unknown>;
} {
    if (items.length === 0) {
        return {
            matches: true,
            vars
        };
    }
    if (interactions.length === 0) {
        return {
            matches: false,
            vars
        };
    }
    const lastItem = items[items.length - 1];
    const lastInteraction = interactions[interactions.length - 1];
    const matchesLastInteraction = evaluateFilter(
        lastInteraction,
        lastItem.interactionFilter,
        vars
    );
    if (matchesLastInteraction) {
        return evaluateItems(
            interactions.slice(0, -1),
            items.slice(0, -1),
            getExportedValues(lastInteraction, lastItem.interactionFilter, vars)
        );
    } else {
        if (lastItem.nextGapType === 'consecutive') {
            return {
                matches: false,
                vars
            };
        } else {
            return evaluateItems(interactions.slice(0, -1), items, vars);
        }
    }
}

function evaluateFilter(
    interaction: Interaction,
    filter: InteractionFilter,
    vars: Record<string, unknown> = {}
): boolean {
    const compiled = Handlebars.compile(filter.filterExpression);
    return (
        interaction.type === filter.interactionType &&
        compiled({
            interaction,
            vars,
            cart: store.getState().cartState.currentCart,
            freeShippingPolicy
        }).trim() === 'true'
    );
}

function evaluateHash(
    scenario: ActiveHelpScenario,
    interaction: Interaction,
    vars: Record<string, unknown> = {}
): string {
    const { hashExpression } = scenario.disabling;
    if (!hashExpression) {
        return scenario.name.trim();
    }
    const compiled = Handlebars.compile(hashExpression);
    return compiled({
        interaction,
        vars,
        cart: store.getState().cartState.currentCart,
        freeShippingPolicy
    }).trim();
}

async function proceedScenario(
    scenario: ActiveHelpScenario,
    interaction: Interaction,
    vars: Record<string, unknown> = {}
): Promise<{ success: true; activeHelp: ActiveHelp } | { success: false }> {
    switch (scenario.activeHelpDefinition.activeHelpType) {
        case 'open-main-menu':
            return {
                success: true,
                activeHelp: {
                    activeHelpType: 'open-main-menu',
                    title: getInterpolatedText(
                        scenario.activeHelpDefinition.title,
                        [],
                        vars,
                        interaction
                    ),
                    description: getInterpolatedText(
                        scenario.activeHelpDefinition.description,
                        [],
                        vars,
                        interaction
                    ),
                    iconId: scenario.activeHelpDefinition.iconId,
                    featureOptions: null
                }
            };
        case 'product-detail':
            return {
                success: true,
                activeHelp: {
                    activeHelpType: 'product-detail',
                    title: getInterpolatedText(
                        scenario.activeHelpDefinition.title,
                        [],
                        vars,
                        interaction
                    ),
                    description: getInterpolatedText(
                        scenario.activeHelpDefinition.description,
                        [],
                        vars,
                        interaction
                    ),
                    iconId: scenario.activeHelpDefinition.iconId,
                    featureOptions: {
                        featureType: 'product-detail',
                        source: 'brilliant',
                        initialQuestion: getInterpolatedText(
                            scenario.activeHelpDefinition.questionText || '',
                            [],
                            vars,
                            interaction
                        ),
                        productId: getInterpolatedText(
                            scenario.activeHelpDefinition.productId,
                            [],
                            vars,
                            interaction
                        )
                    }
                }
            };
        case 'product-search-combinations': {
            const combinationWithProductIds = JSON.parse(
                getInterpolatedText(
                    scenario.activeHelpDefinition.combinationWithProductIds,
                    [],
                    vars,
                    interaction
                )
            ) as string[];
            const response = (
                await apiClient.product.productSearchCombinations({
                    body: {
                        productIds: combinationWithProductIds,
                        maxGroups: 4,
                        maxGroupSize: 4,
                        sortBy: 'relevance'
                    }
                })
            ).jsonResult;
            const totalProducts = response.groups.reduce(
                (previous, current) => previous + current.totalQuantity,
                0
            );
            let productsSample = response.groups.flatMap(g =>
                g.products.slice(0, 1)
            );
            if (productsSample.length === 0) {
                return { success: false };
            }
            if (productsSample.length < 4) {
                productsSample.push(
                    ...response.groups.flatMap(g =>
                        g.products.slice(1, 4 - productsSample.length)
                    )
                );
                productsSample = productsSample.slice(0, 4);
            }

            return {
                success: true,
                activeHelp: {
                    activeHelpType: 'product-search-combinations',
                    title: getInterpolatedText(
                        scenario.activeHelpDefinition.title,
                        productsSample,
                        vars,
                        interaction
                    ),
                    description: getInterpolatedText(
                        scenario.activeHelpDefinition.description,
                        productsSample,
                        vars,
                        interaction
                    ),
                    iconId: scenario.activeHelpDefinition.iconId,
                    productsSample,
                    totalProducts,
                    featureOptions: {
                        featureType: 'product-search-combinations',
                        combinationWithProductIds
                    }
                }
            };
        }
    }
    return { success: false };
}

function getExportedValues(
    interaction: Interaction,
    filter: InteractionFilter,
    vars: Record<string, unknown> = {}
): Record<string, unknown> {
    return filter.exportedValues.reduce((previous, current) => {
        const compiled = Handlebars.compile(current.getterExpression);
        return {
            ...previous,
            [current.key]: castToType(
                compiled({
                    interaction,
                    vars,
                    cart: store.getState().cartState.currentCart,
                    freeShippingPolicy
                }),
                current.type
            )
        };
    }, vars);
}

function getInterpolatedText(
    template: string,
    productList: Product[],
    vars: Record<string, unknown> = {},
    interaction: Interaction
): string {
    const compiled = Handlebars.compile(template);
    return compiled({
        interaction,
        vars,
        cart: store.getState().cartState.currentCart,
        freeShippingPolicy,
        productList
    });
}

function castToType(value: string, type: 'number' | 'string'): unknown {
    if (type === 'number') {
        return +value || 0;
    }
    return value.trim();
}

export function addExecutedScenario(
    hash: string,
    sessionId: string,
    scenarioName: string,
    vars: Record<string, unknown>
): Promise<number> {
    return getDb().add('executedScenarios', {
        hash,
        sessionId,
        executedAt: new Date(),
        name: scenarioName,
        vars
    });
}
async function isEnabledScenario(
    scenario: ActiveHelpScenario,
    hash: string,
    sessionId: string
): Promise<boolean> {
    const executedScenarios = await getDb().getAllFromIndex(
        'executedScenarios',
        'by-hash',
        hash
    );
    if (executedScenarios.length === 0) {
        return true;
    }
    const lastExecutedScenario = executedScenarios.reduce(
        (previous, current) =>
            previous.executedAt.getTime() > current.executedAt.getTime()
                ? previous
                : current,
        executedScenarios[0]
    );
    const disabling = scenario.disabling;

    if (disabling.timeWindow.type === 'session') {
        return sessionId !== lastExecutedScenario.sessionId;
    }
    return (
        new Date().getTime() - lastExecutedScenario.executedAt.getTime() >
        disabling.timeWindow.durationMilliseconds
    );
}
